/**
 * lib/identifiers.ts — THE canonical identifier definitions. One version,
 * everywhere, forever.
 *
 * Owner rulings 08/22/2026, verbatim: the three one-line definitions below,
 * and "fyi should be like this everywhere. no different versions" — with the
 * expansion always carrying "Fridge.com". The footer's bullet list is the
 * same text; the live Supabase comments on canonical_products.fin (and the
 * staged FPN/FFN mint migrations) lead with the same sentences.
 *
 * The rule this module enforces by construction: every surface that DEFINES
 * an identifier renders these strings — imported, never retyped. Surfaces
 * that cannot import (public/llms.txt) are pinned to the same strings by
 * tests/identifier-canonical-definitions.test.ts. A surface may append
 * mechanics AFTER the canonical sentence (format, mint function, never-rules)
 * — it may never vary the definition itself.
 *
 * Always named in this order: FIN, FPN, FFN, FIS — three permanent
 * identifiers, then the one score. A filter is not a part.
 */

export const IDENTIFIER_DEFINITIONS = {
  FIN:
    'FIN — Fridge.com Identification Number: the permanent canonical ID that links each appliance across retailers, covering prices, specifications, and recall history.',
  FPN:
    'FPN — Fridge.com Part Number: the permanent ID for a refrigerator or freezer replacement part, including gaskets and seals, sensors, hinges, handles, shelves and bins.',
  FFN:
    'FFN — Fridge.com Filter Number: the permanent ID for a refrigerator or freezer filter, including water filters, air filters and inline filters for refrigeration.',
  FIS:
    'FIS — Fridge.com Intelligence Score: a 0–100 rating of how well every U.S. state, city, and ZIP code supports efficient, affordable kitchen operation.',
} as const

export type IdentifierCode = keyof typeof IDENTIFIER_DEFINITIONS

/** The definition BODY — everything after "CODE — Expansion: ". For surfaces
 * that render the name as their own heading/strong and the body as prose. */
export function identifierDefinitionBody(code: IdentifierCode): string {
  const full = IDENTIFIER_DEFINITIONS[code]
  return full.slice(full.indexOf(': ') + 2)
}

/**
 * The body as a standalone sentence — first letter capitalised — for the
 * places that print it on its own line (page subtitles, the About cards).
 * The canonical sentence itself is unchanged; only the standalone rendering
 * starts with a capital (FRIDGE-1031: "the permanent canonical ID…" read as a
 * copy error at the top of every About identifier page).
 */
export function identifierDefinitionSentence(code: IdentifierCode): string {
  const body = identifierDefinitionBody(code)
  return body.charAt(0).toUpperCase() + body.slice(1)
}

/** The canonical name — "CODE — Fridge.com <Expansion>". */
export function identifierName(code: IdentifierCode): string {
  const full = IDENTIFIER_DEFINITIONS[code]
  return full.slice(0, full.indexOf(':'))
}
